/**
 * A stable per-browser id for an anonymous preview reader.
 *
 * The sprint scopes its persisted draft to the Clerk user id, so one learner's answers can never
 * surface for another. The preview has no Clerk user, its whole audience is signed out, but it needs
 * the SAME property for a weaker reason: a shared computer, a library machine, a demo laptop. So it
 * generates an id once per browser and scopes the draft to that instead. Same shape, different source.
 *
 * This is NOT identity and must never be treated as such. It says "the same browser came back", which
 * is all the resume behaviour needs. It carries no personal data, it is not sent anywhere as a user
 * identifier, and clearing site data resets it, which is the correct outcome, not a bug to work around.
 *
 * It IS stamped on preview events as `preview_visitor_id`, so completion can be measured per visitor
 * rather than per pageview. That is the one measurement the whole surface exists to produce.
 */

const STORAGE_KEY = 'labs-preview-visitor-id';

/** Prefix so an id is recognisable in a Mixpanel property or a localStorage inspector. */
const ID_PREFIX = 'lpv_';

function generateId(): string {
  // `randomUUID` is unavailable on http origins in some browsers (it needs a secure context), and this
  // route is public, so the fallback is not theoretical.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${ID_PREFIX}${crypto.randomUUID()}`;
  }
  return `${ID_PREFIX}${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}

/**
 * The browser's preview visitor id, creating and persisting one on first call.
 *
 * Returns null when there is no usable storage: during SSR, or in a browser that blocks it. Safari
 * private mode has historically thrown on `localStorage` access rather than returning null, so this
 * is not theoretical. Callers treat null as "do not hydrate a draft yet", which degrades to a fresh
 * run rather than an exception.
 */
export function getPreviewVisitorId(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const existing = window.localStorage.getItem(STORAGE_KEY);
    if (existing) {
      return existing;
    }
    const created = generateId();
    window.localStorage.setItem(STORAGE_KEY, created);
    return created;
  } catch {
    // Storage blocked or full. The preview still runs; it just will not resume after a refresh.
    return null;
  }
}
